<template>
  <div class="shop-items">
    <div class="shop-items__labels">
      <div>{{ $t('shopDefaultProduct') }}</div>
      <div>{{ $t('shopProductUpgrade') }}</div>
    </div>
    <Draggable
      :value="products"
      @input="movement"
      :disabled="readonly"
      class="shop-items__container"
      draggable=".draggable"
      direction="horizontal"
      ghost-class="ghost"
      :animation="150"
    >
      <template #header>
        <Item
          v-if="!defaultProduct"
          key="default"
          :value="{ selected: !hasSelection }"
          :readonly="readonly"
          :placeholder="true"
          @click="!readonly && addDialog({ multiple: false, isDefault: true })"
        >
          <template #placeholder>
            <div class="col center middle">
              <div class="col center middle">
                <FontAwesomeIcon :icon="['fal', 'cube']" size="2x" class="mb-1" />
                <div>{{ $t('noShopDefaultProduct') }}</div>
              </div>
              <div v-if="!readonly" class="shop-items__item-placeholder-add">
                <span>{{ $t('shopAddProduct') }}</span>
                <FontAwesomeIcon :icon="['fal', 'plus']" class="ml-1" />
              </div>
            </div>
          </template>
        </Item>
      </template>

      <Item
        v-for="(item, index) in products"
        :key="'item' + index"
        :value="item"
        :area="area"
        :readonly="readonly"
        :defaultProduct="defaultProduct"
        :pricing-method="pricingMethod"
        :categories="categories"
        class="draggable"
        @select="select(item)"
        @unselect="select(null)"
        @remove="remove(item)"
        @move="product => $emit('move', product)"
        @copy="product => actionHandler('copy', { product, item })"
        @edit="product => actionHandler('edit', { product, item })"
      />

      <template #footer>
        <template v-if="!readonly">
          <Item
            v-for="n in Math.max(1, (defaultProduct ? 4 : 3) - value.length)"
            :key="'ph_' + n"
            :placeholder="$t('shopAddProduct')"
            @click="addDialog"
          />
        </template>
      </template>
    </Draggable>
    <ProductSelect
      :key="filterHash"
      ref="selector"
      :items="getProducts"
      :is-disabled="isDisabled"
      v-bind="selectorOptions"
      @select="setActionStatus"
      type="modal-only"
      required
      class="shop-items__selector"
    >
      <template #actions>
        <ButtonComponent
          :disabled="hasItemsSelected"
          theme="secondary"
          :label="$t('createProduct')"
          :icon="['fal', 'plus']"
          data-action="create"
          @click="actionHandler('create')"
        />
      </template>
    </ProductSelect>
    <Portal>
      <Modal :show="Boolean(createOptions)" @update:show="createOptions = undefined">
        <ProductFormWrapper
          :id="id"
          :create-options="createOptions"
          @submit="submitProduct"
          @reset-options="createOptions = undefined"
        />
      </Modal>
    </Portal>
  </div>
</template>

<script>
import ObjectId from 'mongo-objectid'
import Hash from 'object-hash'
import ProductApi from '@/api/Product'
import { PartialUpdate } from '@/utils'

import Model from '@/models/Shop/item'
import ProductModel from '@/models/Product'
import Draggable from 'vuedraggable'
import { ModalComponent as Modal } from 'vue-elder-modal'
import Portal from '@/components/Portal'

export default {
  inject: ['shopTracker'],
  props: {
    value: Array,
    area: Number,
    readonly: Boolean,
    pricingMethod: String,
    categories: Array,
  },
  data() {
    return {
      selectorOptions: { multiple: true, search: true },
      createOptions: null,
      addOptions: null,
      hasItemsSelected: false,
    }
  },
  computed: {
    id() {
      if (!this.createOptions) return
      return (this.createOptions.product || {}).id
    },
    defaultProduct() {
      return this.value.find(v => v.isDefault)
    },
    products() {
      return this.value.sort((a, b) => {
        return a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1
      })
    },
    hasSelection() {
      return Boolean(this.value.filter(v => v.selected).length)
    },
    filterHash() {
      return Hash(this.value)
    },
  },
  methods: {
    movement(items) {
      this.$emit(
        'input',
        items.map((item, index) => {
          // Keep default selected if selected
          if (this.defaultProduct && this.defaultProduct.selected) {
            if (index === 0) item.selected = true
            else item.selected = false
          }

          if (index === 0 && this.defaultProduct) {
            item.isDefault = true
            item.conditions = { if: [], ifnot: [] }
            item.quantity = {
              enabled: false,
              min: 1,
              max: null,
            }
            item.referencePropertyChanges = []
          } else {
            item.isDefault = false
          }

          return item
        }),
      )
      this.shopTracker('ProductReorder')
    },
    setActionStatus(item) {
      this.hasItemsSelected = Boolean(item && item.length)
    },
    addDialog(options = {}) {
      let { multiple = true, isDefault = false } = options
      this.addOptions = { multiple, isDefault }
      this.selectorOptions.multiple = multiple

      this.$refs.selector.$children[0].open()
      this.$refs.selector.$once('input', i => this.add(i, options))
    },
    edit(item) {
      this.value.forEach(v => {
        if (v.product.id !== item.id) return
        PartialUpdate(v.product, item)
      })
    },
    setSelected(items) {
      if (items.every(r => !r.selected)) {
        let defaultProduct = items.find(r => r.isDefault)
        if (defaultProduct) defaultProduct.selected = true
      }
    },
    copy(product, item) {
      let result = [
        ...this.value,
        Model({
          ...item,
          sets: [],
          id: new ObjectId().hex,
          product: { ...item.product, ...product, isScoped: true },
          isDefault: false,
          selected: false,
        }),
      ]

      this.$emit('input', result)
    },
    add(items, options = {}) {
      let { isDefault = false } = options
      items = items instanceof Array ? items : [items]

      let result = [
        ...this.value,
        ...items.map(item =>
          Model({ id: new ObjectId().hex, product: item, isDefault, overrides: { price: isDefault ? 0 : undefined } }),
        ),
      ]

      this.setSelected(result)
      this.$emit('input', result)
    },
    actionHandler(action, data = {}) {
      let { product, item } = data

      const reference = {
        onModel: this.$store.state.Shop.item.reference.__typename,
        ref: this.$store.state.Shop.item.reference.id,
      }

      switch (action) {
        case 'edit':
          this.addOptions = { isDefault: product.id === (this.defaultProduct && this.defaultProduct.product.id) }
          break
        case 'copy':
          product = { ...product, id: undefined, reference }
          this.addOptions = { isDefault: false }
          break
        case 'create':
        default:
          product = null
          break
      }

      this.createOptions = {
        action: action || 'create',
        isDefault: this.addOptions.isDefault,
        promise: null,
        item,
        product: product
          ? ProductModel({
              ...product,
              ...(product.isScoped ? { reference } : {}),
            })
          : ProductModel({
              reference,
              pricing: {
                vat: this.$store.state.enums.Region.vat.default,
              },
            }),
      }
    },
    submitProduct(productData) {
      let data = ProductModel(productData, {
        transform: {
          products: val => val && val.map(v => v.id),
        },
      })

      this.createOptions.promise = ProductApi.mutate(data).then(res => {
        const product = { ...productData, ...res }
        const { isDefault, item } = this.createOptions
        const options = { isDefault }

        const handlers = {
          create: () => this.add(product, options),
          copy: () => this.copy(product, item, options),
          edit: () => this.edit(product),
        }

        handlers[this.createOptions.action]()

        this.createOptions = null
        this.$refs.selector.$children[0].close()
      })
    },
    remove(item) {
      let result = this.value.filter(v => v !== item)
      if (item.selected) result = result.map(v => ({ ...v, selected: v.isDefault }))

      this.$emit('input', result)
    },
    select(item) {
      if (!item && this.defaultProduct) item = this.defaultProduct

      this.$emit(
        'input',
        this.value.map(v => ({ ...v, selected: v === item })),
      )
    },
    getProducts(filter) {
      return ProductApi.get(
        { filter: { ...filter, references: [this.$store.state.Shop.item.reference.id] }, limit: 50 },
        { query: 'Products' },
      )
    },
    isDisabled(item) {
      return this.value.find(v => v.product.id === item.id) ? true : false
    },
  },
  components: {
    Item: () => import('./Item'),
    ProductSelect: () => import('@/components/Product/Select/Modal'),
    ProductFormWrapper: () => import('@/components/Product/FormWrapper'),
    Draggable,
    Modal,
    Portal,
  },
}
</script>

<style lang="scss">
.shop-items {
  &__labels,
  &__container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    grid-gap: 2rem;
  }

  &__labels {
    & > div {
      opacity: 0.4;
      margin-bottom: 1rem;
    }
  }

  &__item-placeholder-add {
    margin-top: 1rem;
    padding: 0.25rem 1rem;

    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-md);
  }

  &__selector {
    .select-modal__modal-card {
      width: 100%;
      max-width: 1100px;
    }

    [data-action='cancel'] {
      order: 1;
      margin-right: auto;
    }

    [data-action='create'] {
      order: 2;
    }

    [data-action='confirm'] {
      order: 3;
    }
  }

  .shop-item {
    position: relative;
    transition-duration: 150ms;
    transition-property: opacity, filter;
    transition-timing-function: ease-out;

    &.ghost {
      opacity: 0.25;
      filter: grayscale(0%);
    }

    &.sortable-chosen {
      .product-card__selection {
        opacity: 0;
      }
    }
  }
}
</style>
